import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import React__default, { useState } from 'react';
import { styled, css } from 'styled-components';
import { useResourceDataContextSafe } from '../context/resourceDataContext.js';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { isResourceStateProcessing } from '../../../resources/utils/isResourceStateProcessing.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { cssTextEllipsis, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { MagicRename } from '../../../magic/components/MagicRename.js';
import { useAnimatedText } from '../../../magic/hooks/useAnimatedText.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { FaviconImage } from '../FaviconImage.js';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { detectMobile } from '@fabric-app/utils/detection';
import { getOriginUrlWithHash } from '../../../resources/utils/getOriginUrlWithHash.js';
import { getResourceNamePlaceholder } from '../../../resources/utils/getResourceTitle.js';
import HoverItem from '../HoverItem.js';
import { isResourceDetailBookmark, isResourceDetailHighlight, isResourceDetailImageBookmark } from '../../../resources/utils/resourceDetailGuards.js';
import { P } from '@fabric-app/ui/components/Typography';

const FONT_SIZE = "1rem";
const StyledFaviconImage = styled(FaviconImage).withConfig({
  displayName: "ResourceTitleEditable__StyledFaviconImage",
  componentId: "sc-7b46b79d-0"
})([
  `width:23px;height:23px;cursor:pointer;`
]);
const Input = styled.input.withConfig({
  displayName: "ResourceTitleEditable__Input",
  componentId: "sc-7b46b79d-1"
})([
  `border:none;background:none;outline:none;padding:0;color:`,
  `;font-weight:500;`,
  `;min-width:1px;position:relative;z-index:10;height:19px;`,
  ``
], cssVar["color-text-primary"], cssTextEllipsis, (p) => p.weight && css([
  `font-weight:`,
  `;`
], p.weight));
const HeaderExtension = styled.div.withConfig({
  displayName: "ResourceTitleEditable__HeaderExtension",
  componentId: "sc-7b46b79d-2"
})([
  `flex-shrink:0;user-select:none;padding:0;flex-shrink:0;font-size:`,
  `;color:`,
  `;transform:translateX(-1px);`
], FONT_SIZE, cssVar["color-text-quaternary"]);
const InputContainer = styled.div.withConfig({
  displayName: "ResourceTitleEditable__InputContainer",
  componentId: "sc-7b46b79d-3"
})([
  `min-width:1px;display:flex;align-items:center;padding:2px 4px 2px 4px;border-radius:6px;position:relative;`,
  ``
], (p) => !p.readOnly && css([
  ``,
  `{&:hover{background:`,
  `;}}&:focus-within{background:`,
  `;}`
], mediaHover, cssVar["color-bg-tertiary"], cssVar["color-bg-tertiary"]));
const Container = styled(Flex).attrs({
  direction: "column",
  minWidth: 1
}).withConfig({
  displayName: "ResourceTitleEditable__Container",
  componentId: "sc-7b46b79d-4"
})([
  `gap:0.125rem;`
]);
const HiddenReferenceWidthTitle = styled.div.withConfig({
  displayName: "ResourceTitleEditable__HiddenReferenceWidthTitle",
  componentId: "sc-7b46b79d-5"
})([
  `position:absolute;opacity:0;pointer-events:none;min-width:150px;width:max-content;font-size:`,
  `;font-weight:500;height:19px;white-space:pre;border-radius:6px;`
], FONT_SIZE);
const isHoverEventValid = (e) => {
  const target = e.target;
  if (target.closest('[data-no-hover-link="true"]')) return false;
  if (!e.currentTarget.contains(target)) return false;
  return true;
};
const ResourceTitleEditable = ({ titleInput, user, setIsEditorFocused, readOnly = false, onSubmit, resourceUrl }) => {
  const titleRef = React__default.useRef(null);
  const inputRef = React__default.useRef(null);
  const { isMobileView } = useResponsive();
  const { resource } = useResourceDataContextSafe();
  const showHoverLinkState = useBoolState(false);
  const [hoverPos, setHoverPos] = React__default.useState({
    x: 0,
    y: 0
  });
  const isMobile = Boolean(detectMobile(true));
  const onMouseMoveOverTitle = (e) => {
    if (isMobile) return;
    if (!isHoverEventValid(e)) {
      showHoverLinkState.handleFalse();
      return;
    }
    const { x, y } = e.nativeEvent;
    setHoverPos({
      x,
      y
    });
    showHoverLinkState.handleTrue();
  };
  const gotoExternalLink = (e) => {
    if (!resourceUrl || !isHoverEventValid(e)) return;
    window.open(getOriginUrlWithHash(resourceUrl, resource.id), "_blank");
    showHoverLinkState.handleFalse();
  };
  const onMouseEnter = (e) => {
    if (!isHoverEventValid(e)) {
      return;
    }
    showHoverLinkState.handleTrue();
  };
  const onMouseLeave = () => {
    showHoverLinkState.handleFalse();
  };
  const [isInputFocused, setIsInputFocused] = React__default.useState(false);
  React__default.useEffect(() => {
    const titleRefEl = titleRef.current;
    const inputRefEl = inputRef.current;
    if (!titleRef || !inputRef || !titleRefEl || !inputRef?.current) {
      return;
    }
    const updateWidth = () => {
      inputRefEl.style.width = `${titleRefEl.offsetWidth + 1}px`;
    };
    const observer = new MutationObserver(updateWidth);
    const resizeObserver = new ResizeObserver(updateWidth);
    observer.observe(titleRefEl, {
      attributes: true,
      childList: true,
      subtree: true
    });
    resizeObserver.observe(titleRefEl);
    updateWidth();
    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [
    inputRef,
    titleRef
  ]);
  const refocusTitleInput = React__default.useCallback((e) => {
    if (isMobileView) {
      setIsInputFocused((state) => !state);
      return;
    }
    const inputRefEl = inputRef?.current;
    if (!inputRefEl || e.target !== e.currentTarget) return;
    inputRefEl.focus();
    const { length } = inputRefEl.value;
    inputRefEl.setSelectionRange(length, length);
  }, [
    inputRef,
    isMobileView
  ]);
  const extension = resource.extension;
  const placeholder = getResourceNamePlaceholder(resource);
  const { isAnimating, addAnimation, onAnimateText, inputStyle } = useAnimatedText({
    updateRealText: titleInput.set,
    floatingTextStyle: isMobileView ? {
      padding: "0.27rem 0.375rem",
      fontWeight: 500
    } : {},
    overflows: isMobileView
  });
  const [isRenaming, setIsRenaming] = useState(false);
  const sharedInputProps = {
    readOnly: readOnly || isAnimating || isRenaming,
    ["data-or-obscured"]: true,
    value: titleInput.value,
    onChange: titleInput.onChange,
    placeholder,
    /**
    * on key down enter - we blur
    * on blur, we trigger submit
    */
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.currentTarget.blur();
      }
    },
    style: {
      ...inputStyle,
      ...isRenaming ? {
        animation: "soft-blink 2s infinite"
      } : {}
    }
  };
  const magicRenameEnabled = !resource.isDraft && !isAnimating && !isResourceStateProcessing(resource.stateProcessing) && !readOnly;
  const faviconUrl = isResourceDetailBookmark(resource) ? resource.data.webpage?.favicon?.url : void 0;
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      !isMobile && resourceUrl && !isInputFocused && /* @__PURE__ */ jsx(HoverItem, {
        faviconUrl,
        setShow: showHoverLinkState.handleTrue,
        show: showHoverLinkState.value,
        fdocId: resource.id,
        title: titleInput.value.length > 0 ? titleInput.value : isResourceDetailHighlight(resource) || isResourceDetailBookmark(resource) || isResourceDetailImageBookmark(resource) ? resource.data.webpage?.title ?? placeholder : placeholder,
        url: resourceUrl,
        x: hoverPos.x,
        y: hoverPos.y
      }),
      isMobileView && !readOnly && /* @__PURE__ */ jsx(Modal.Root, {
        open: isInputFocused || isAnimating || isRenaming,
        onOpenChange: (v) => {
          if (!v) {
            setIsEditorFocused?.(false);
            setIsInputFocused(false);
          }
        },
        children: /* @__PURE__ */ jsxs(Modal.Portal, {
          container: getDefaultRadixPortalContainer(),
          children: [
            /* @__PURE__ */ jsx(Modal.Overlay, {
              visibleOnMobileViewport: true
            }),
            /* @__PURE__ */ jsxs(ModalContent, {
              id: "edit-title",
              children: [
                /* @__PURE__ */ jsxs(VisuallyHidden, {
                  children: [
                    /* @__PURE__ */ jsx(DialogTitle, {
                      children: "Title"
                    }),
                    /* @__PURE__ */ jsx(DialogDescription, {
                      children: "Edit the title of the resource"
                    })
                  ]
                }),
                /* @__PURE__ */ jsx(MagicRename, {
                  resourceId: magicRenameEnabled && isMobileView ? resource.id : void 0,
                  onRename: onAnimateText,
                  onFetch: () => setIsRenaming(true),
                  onSettled: () => setIsRenaming(false),
                  children: /* @__PURE__ */ jsx(TextAreaAutosizeWrapper, {
                    "data-replicated-value": titleInput.value,
                    hideText: isAnimating,
                    children: addAnimation(/* @__PURE__ */ jsx("textarea", {
                      autoFocus: true,
                      onFocus: (e) => {
                        setIsEditorFocused?.(true);
                        setIsInputFocused(true);
                        e.currentTarget.setSelectionRange(titleInput.value.length, titleInput.value.length);
                      },
                      onBlur: () => {
                        onSubmit?.(titleInput.value);
                        setIsEditorFocused?.(false);
                        setIsInputFocused(false);
                      },
                      ...sharedInputProps
                    }), !isMobileView)
                  })
                })
              ]
            })
          ]
        })
      }),
      /* @__PURE__ */ jsxs(Container, {
        direction: "column",
        gap: "text",
        onClick: gotoExternalLink,
        onMouseEnter,
        onMouseLeave,
        onMouseMove: onMouseMoveOverTitle,
        children: [
          /* @__PURE__ */ jsxs(Flex, {
            gap: "text",
            alignItems: "center",
            minWidth: 1,
            children: [
              resourceUrl && /* @__PURE__ */ jsx(StyledFaviconImage, {
                faviconUrl,
                pageUrl: resourceUrl,
                sizeToLoad: 32
              }),
              /* @__PURE__ */ jsx(MagicRename, {
                resourceId: magicRenameEnabled && !isMobileView ? resource.id : void 0,
                onRename: onAnimateText,
                onFetch: () => setIsRenaming(true),
                onSettled: () => setIsRenaming(false),
                children: /* @__PURE__ */ jsxs(InputContainer, {
                  "data-no-hover-link": "true",
                  style: {
                    cursor: user?.id === resource.user?.id ? "text" : "default"
                  },
                  onClick: refocusTitleInput,
                  readOnly,
                  children: [
                    /* @__PURE__ */ jsx(HiddenReferenceWidthTitle, {
                      ref: titleRef,
                      style: {
                        minWidth: extension ? "1em" : 120
                      },
                      children: titleInput.value.length > 0 ? titleInput.value : placeholder
                    }),
                    addAnimation(/* @__PURE__ */ jsx(Input, {
                      ref: inputRef,
                      type: "text",
                      onFocus: () => {
                        setIsEditorFocused?.(true);
                        setIsInputFocused(true);
                      },
                      tabIndex: resource.kind === "notepad" ? 10 : 4,
                      onBlur: () => {
                        onSubmit?.(titleInput.value);
                        setIsEditorFocused?.(false);
                        if (!isMobileView) {
                          setIsInputFocused(false);
                        }
                      },
                      weight: resourceUrl ? 400 : void 0,
                      ...sharedInputProps
                    }), isMobileView),
                    extension && /* @__PURE__ */ jsxs(HeaderExtension, {
                      onClick: refocusTitleInput,
                      children: [
                        ".",
                        extension
                      ]
                    })
                  ]
                })
              })
            ]
          }),
          resourceUrl && /* @__PURE__ */ jsxs(UrlContainer, {
            "max-width": "100%",
            children: [
              /* @__PURE__ */ jsx(UrlParagraph, {
                style: {
                  userSelect: "none"
                },
                children: resourceUrl
              }),
              /* @__PURE__ */ jsx(UrlContainerBuffer, {
                "data-no-hover-link": "true"
              })
            ]
          })
        ]
      })
    ]
  });
};
const UrlContainer = styled(Flex).withConfig({
  displayName: "ResourceTitleEditable__UrlContainer",
  componentId: "sc-7b46b79d-6"
})([
  `padding:2px 4px;`
]);
const UrlContainerBuffer = styled.div.withConfig({
  displayName: "ResourceTitleEditable__UrlContainerBuffer",
  componentId: "sc-7b46b79d-7"
})([
  `flex-grow:1;flex-shrink:1;`
]);
const UrlParagraph = styled(P).attrs({
  color: "tertiary",
  size: "xs",
  weight: 500
}).withConfig({
  displayName: "ResourceTitleEditable__UrlParagraph",
  componentId: "sc-7b46b79d-8"
})([
  `color:rgba(`,
  `,0.5);line-height:normal;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`,
  `;`
], cssVar["color-text-primary-rgb"], cssTextEllipsis);
const ModalContent = styled(Modal.ContentRaw).withConfig({
  displayName: "ResourceTitleEditable__ModalContent",
  componentId: "sc-7b46b79d-9"
})([
  `position:fixed;top:0;inset:0px;z-index:126;display:flex;flex-direction:column;padding:0 14px;padding-top:calc(var(--safe-margin-offset-top) + 15.5px);`
]);
const TextAreaAutosizeWrapper = styled.div.withConfig({
  displayName: "ResourceTitleEditable__TextAreaAutosizeWrapper",
  componentId: "sc-7b46b79d-10"
})([
  `width:100%;display:grid;color:`,
  `;& > textarea{resize:none;overflow:hidden;opacity:0.3 !important;}& > textarea,&::after{font:inherit;grid-area:1 / 1 / 2 / 2;padding:0.25rem 0.375rem;overflow-x:hidden;font-weight:500;background:`,
  `;border:none;outline:none;color:`,
  `;border-radius:6px;overflow:hidden;width:100%;white-space:pre-wrap;line-break:anywhere;}&::after{content:attr(data-replicated-value) ' ';}`,
  ``
], cssVar["color-text-primary"], cssVar["color-bg-tertiary"], cssVar["color-text-primary"], ({ hideText }) => hideText && css([
  `& > textarea,&::after{color:#00000000;}`
]));

export { ResourceTitleEditable };
//# sourceMappingURL=ResourceTitleEditable.js.map
